import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Container, Paper, Typography } from '@mui/material';
import { Assignment } from '@mui/icons-material';
import PerformanceCard from '../../components/PerformanceCard/PerformanceCard';
import { OrdersTable, OpportunitiesTable } from '../../components/DataTable/examples';
import styles from './DashboardPage.module.css';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchOrders } from '../../redux/slices/orderSlice';
import { fetchOpportunities } from '../../redux/slices/opportunitySlice';
import { RootState } from '../../redux/store';
import { Order, Opportunity } from '../../types';
import { getCustomerStats, CustomerStats } from '../../services/customer-service';
import {
  getProcessingOrders,
  ProcessingOrdersStats,
  getCompletedOrdersValue,
  CompletedOrdersValueStats,
} from '../../services/order-service';

// Extended Order interface with additional properties needed
interface ExtendedOrder extends Order {
  customer?: {
    company_name: string;
    [key: string]: unknown;
  };
  quote?: {
    id: string;
    code: string;
    status: string;
    expiration_date: string;
    [key: string]: unknown;
  };
  created_at?: string;
  order_items?: Array<{
    product_id: string;
    quantity: number;
    unit_price: number;
    discount_percent?: number;
    discount_amount?: number;
    id: string;
    order_id: string;
  }>;
}

const DashboardPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { items: orders, loading } = useAppSelector((state: RootState) => state.orders);
  const { items: opportunities, loading: opportunitiesLoading } = useAppSelector(
    (state: RootState) => state.opportunities
  );
  const [customerStats, setCustomerStats] = useState<CustomerStats>({
    total_customers: 0,
    current_month_customers: 0,
    previous_month_customers: 0,
    month_percentage: 0,
  });
  const [processingOrdersStats, setProcessingOrdersStats] = useState<ProcessingOrdersStats>({
    orders: [],
    total_processing_count: 0,
    month_percentage: 0,
  });
  const [completedOrdersValueStats, setCompletedOrdersValueStats] =
    useState<CompletedOrdersValueStats>({
      total_value_last_month: 0,
      previous_month_percentage: 0,
    });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    // For the dashboard, we just reset to default (Home only)
    setBreadcrumbs([{ label: 'Home', path: '/home' }]);

    // Fetch orders and opportunities when component mounts
    dispatch(fetchOrders());
    dispatch(fetchOpportunities());

    // Fetch customer stats
    const fetchCustomerStats = async () => {
      try {
        const response = await getCustomerStats();
        if (response.success && response.data) {
          setCustomerStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching customer stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    // Fetch processing orders stats
    const fetchProcessingOrdersStats = async () => {
      try {
        const response = await getProcessingOrders();
        if (response.success && response.data) {
          setProcessingOrdersStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching processing orders stats:', error);
      }
    };

    // Fetch completed orders value stats
    const fetchCompletedOrdersValueStats = async () => {
      try {
        const response = await getCompletedOrdersValue();
        if (response.success && response.data) {
          setCompletedOrdersValueStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching completed orders value stats:', error);
      }
    };

    fetchCustomerStats();
    fetchProcessingOrdersStats();
    fetchCompletedOrdersValueStats();

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, dispatch]);

  // Helper function to calculate total
  const calculateTotal = (order: ExtendedOrder): string => {
    if (!order.order_items || order.order_items.length === 0) {
      return '0,00';
    }

    const total = order.order_items.reduce((sum: number, item) => {
      return sum + item.quantity * item.unit_price;
    }, 0);

    // Format as Brazilian currency
    return total.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Transform order data for table (similar to OrdersPage)
  const orderTableData = orders.slice(0, 4).map(order => {
    const extendedOrder = order as ExtendedOrder;
    return {
      id: order.id,
      client: extendedOrder.customer?.company_name || 'Cliente não identificado',
      code: order.id.substring(0, 8),
      quoteCode:
        extendedOrder.quote?.code || (order.quote_id ? order.quote_id.substring(0, 8) : 'N/A'),
      date: new Date(extendedOrder.created_at || '').toLocaleDateString('pt-BR'),
      paymentMethod: order.payment_method,
      paymentTerm: order.payment_term,
      status: order.status,
      value: 'R$ ' + calculateTotal(extendedOrder),
    };
  });

  // Transform opportunities data for the dashboard table
  const opportunityTableData = opportunities.slice(0, 4).map(opportunity => ({
    id: opportunity.id,
    client:
      opportunity.customer?.company_name ||
      opportunity.client_company_name ||
      'Cliente não identificado',
    title: opportunity.title,
    date: new Date(
      opportunity.estimated_closing_date || opportunity.expected_close_date || ''
    ).toLocaleDateString('pt-BR'),
    value: opportunity.value
      ? `R$ ${opportunity.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : 'N/A',
    status:
      opportunity.status === 'Open'
        ? 'Em negociação'
        : opportunity.status === 'Won'
          ? 'Aprovado'
          : 'Rejeitado',
  }));

  const handleViewOrder = (orderId: string): void => {
    navigate(`/orders/${orderId}`);
  };

  const handleViewOpportunity = (opportunityId: string): void => {
    navigate(`/opportunities/${opportunityId}`);
  };

  return (
    <>
      <Typography variant="h5">Resumo de Performance</Typography>

      {/* Performance Cards */}
      <div className={styles.performanceCards}>
        <PerformanceCard
          title="Clientes Ativos"
          value={customerStats.total_customers.toString()}
          percentageChange={
            customerStats.month_percentage > 0
              ? `+${customerStats.month_percentage}%`
              : `${customerStats.month_percentage}%`
          }
          positive={customerStats.month_percentage >= 0}
        />
        <PerformanceCard
          title="Pedidos em Processamento"
          value={processingOrdersStats.total_processing_count.toString()}
          percentageChange={
            processingOrdersStats.month_percentage > 0
              ? `+${processingOrdersStats.month_percentage}%`
              : `${processingOrdersStats.month_percentage}%`
          }
          positive={processingOrdersStats.month_percentage >= 0}
        />
        <PerformanceCard
          title="Faturamento no último mês"
          value={`R$ ${completedOrdersValueStats.total_value_last_month.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`}
          percentageChange={
            completedOrdersValueStats.previous_month_percentage > 0
              ? `+${completedOrdersValueStats.previous_month_percentage}%`
              : `${completedOrdersValueStats.previous_month_percentage}%`
          }
          positive={completedOrdersValueStats.previous_month_percentage >= 0}
        />
        <PerformanceCard
          title="Oportunidades abertas"
          value={`${opportunities.filter(op => op.status === 'Open').length}`}
          percentageChange=""
          blueText={true}
          additionalText={`${opportunities.filter(op => op.status === 'Open').length} em negociação, 
                          ${opportunities.filter(op => op.status === 'Won').length} aprovadas, 
                          ${opportunities.filter(op => op.status === 'Lost').length} rejeitadas`}
        />
      </div>

      {/* Latest Orders */}
      <Paper elevation={1} className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableIconContainer}>
            <img
              src="/icons/ultimos-pedidos.svg"
              alt="Últimos Pedidos"
              className={styles.tableIcon}
            />
          </div>
          <Typography variant="h6" sx={{ fontSize: '18px' }}>
            Últimos Pedidos dos Clientes
          </Typography>
        </div>

        {loading ? (
          <Typography sx={{ padding: 2 }}>Carregando pedidos...</Typography>
        ) : orderTableData.length > 0 ? (
          <OrdersTable data={orderTableData} onViewOrder={handleViewOrder} />
        ) : (
          <Typography sx={{ padding: 2 }}>Nenhum pedido encontrado.</Typography>
        )}

        <div className={styles.tableFooter}>
          <Button
            component={RouterLink}
            to="/orders"
            variant="outlined"
            size="small"
            className={styles.viewAllButton}
            sx={{ height: '30px' }}
          >
            Ver Todos os Pedidos
          </Button>
        </div>
      </Paper>

      {/* Open Opportunities */}
      <Paper elevation={1} className={styles.tableContainer}>
        <div className={styles.tableHeader}>
          <div className={styles.tableIconContainer}>
            <img src="/icons/opportunity.svg" alt="Últimos Pedidos" className={styles.tableIcon} />
          </div>
          <Typography variant="h6" sx={{ fontSize: '18px' }}>
            Oportunidades em Aberto
          </Typography>
        </div>

        {opportunitiesLoading ? (
          <Typography sx={{ padding: 2 }}>Carregando oportunidades...</Typography>
        ) : opportunityTableData.length > 0 ? (
          <OpportunitiesTable data={opportunityTableData} />
        ) : (
          <Typography sx={{ padding: 2 }}>Nenhuma oportunidade encontrada.</Typography>
        )}

        <div className={styles.tableFooterFlex}>
          <Button
            component={RouterLink}
            to="/opportunities"
            variant="outlined"
            size="small"
            className={styles.viewAllButton}
            sx={{ height: '30px' }}
          >
            Ver todas oportunidades
          </Button>
          <Button
            component={RouterLink}
            to="/opportunities/new"
            variant="contained"
            size="small"
            className={styles.newOpportunityButton}
            sx={{ height: '30px' }}
          >
            Registrar nova oportunidade
          </Button>
        </div>
      </Paper>
    </>
  );
};

export default DashboardPage;
